// A manager's organization totals from the assessment module only (spec #86,
// #95): how many beneficiaries have a valid exam and a valid Namat scale, the
// scale's average and band counts, and for each health indicator how many of
// those measured are healthy. Counts only; no names, no individual values.
// Pure; copied identically to mobile and functions/assessment.

import { INDICATORS, type Band, type Indicator, type IndicatorReading, type NamatAssessment } from './namatAssessment';

export interface BeneficiarySummary {
  assessment: Pick<NamatAssessment, 'status' | 'scale' | 'examDone'>;
  indicators: Pick<IndicatorReading, 'key' | 'level'>[];
}

export interface OrgClinicalStats {
  beneficiaries: number;
  /** A completed exam inside the validity window. */
  examDone: number;
  /** A valid Namat scale. */
  scaleDone: number;
  /** Average of the valid scales, rounded; null when there is none. */
  averagePercent: number | null;
  bands: Record<Band, number>;
  /** Per indicator: measured in the latest exam, and of those at the "good" level. */
  indicators: Record<Indicator, { measured: number; healthy: number }>;
}

export function summarizeClinical(summaries: BeneficiarySummary[]): OrgClinicalStats {
  const bands: Record<Band, number> = { excellent: 0, very_good: 0, good: 0, needs_improvement: 0 };
  const indicators = Object.fromEntries(INDICATORS.map((k) => [k, { measured: 0, healthy: 0 }])) as OrgClinicalStats['indicators'];
  let examDone = 0;
  let scaleDone = 0;
  let percentSum = 0;
  for (const { assessment, indicators: readings } of summaries) {
    if (assessment.examDone) examDone++;
    if (assessment.status === 'valid' && assessment.scale) {
      scaleDone++;
      percentSum += assessment.scale.percent;
      bands[assessment.scale.band]++;
    }
    for (const r of readings) {
      if (!r.level) continue;
      indicators[r.key].measured++;
      if (r.level === 'good') indicators[r.key].healthy++;
    }
  }
  return {
    beneficiaries: summaries.length,
    examDone,
    scaleDone,
    averagePercent: scaleDone ? Math.round(percentSum / scaleDone) : null,
    bands,
    indicators,
  };
}

/** Whole-number share, 0 when there is nothing to share out. */
export function share(part: number, whole: number): number {
  return whole > 0 ? Math.round((part / whole) * 100) : 0;
}
